import { useEffect, useRef, useState } from "react";
import type Hls from "hls.js";
import { getHomeVideoSources, homeVideoBase } from "../models/homeVideo";

export function useHomeVideoPresenter(adaptive: boolean) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [hasPlayed, setHasPlayed] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const mobile = window.matchMedia("(max-width: 767px)");
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
    const connection = (navigator as Navigator & { connection?: { saveData?: boolean } }).connection;
    let nearViewport = false;
    let disposed = false;
    let generation = 0;
    let loadedVariant: boolean | null = null;
    let hls: Hls | null = null;
    let loading = false;
    let usingMp4 = false;
    let failed = false;
    let mediaRecovered = false;

    const canPlay = () => nearViewport && !document.hidden && !reducedMotion.matches && !connection?.saveData;
    const play = () => {
      if (!canPlay() || failed || disposed) return;
      if (hls && !loading) {
        hls.startLoad(video.currentTime);
        loading = true;
      }
      // Autoplay can be denied (e.g. iPhone low-power mode). Keep the image fallback.
      void video.play().catch(() => undefined);
    };
    const showFallback = () => {
      failed = true;
      setHasPlayed(false);
      hls?.destroy();
      hls = null;
      video.pause();
      video.removeAttribute("src");
      video.load();
    };
    const loadMp4 = () => {
      if (disposed) return;
      hls?.destroy();
      hls = null;
      if (usingMp4) {
        showFallback();
        return;
      }
      usingMp4 = true;
      video.src = getHomeVideoSources(adaptive && mobile.matches, homeVideoBase).mp4;
      video.load();
      play();
    };
    const initialize = async () => {
      const variant = adaptive && mobile.matches;
      if (!canPlay() || failed || loadedVariant === variant) return;
      loadedVariant = variant;
      const current = ++generation;
      hls?.destroy();
      hls = null;
      loading = false;
      usingMp4 = false;
      mediaRecovered = false;
      setHasPlayed(false);
      video.pause();
      video.removeAttribute("src");
      delete video.dataset.quality;
      video.load();
      const sources = adaptive ? getHomeVideoSources(mobile.matches, homeVideoBase) : { hls: null };
      if (!sources.hls) {
        loadMp4();
        return;
      }
      const appleDevice = navigator.vendor === "Apple Computer, Inc."
        || /iP(hone|ad|od)/.test(navigator.userAgent);
      if (appleDevice && video.canPlayType("application/vnd.apple.mpegurl")) {
        video.src = sources.hls;
        video.load();
        play();
        return;
      }
      try {
        // Download the HLS player only for adaptive video near the viewport.
        // Our silent H.264/fMP4 stream needs neither subtitles nor DRM/alternate audio.
        const { default: HlsPlayer } = await import("hls.js/light");
        if (disposed || current !== generation) return;
        if (!canPlay()) {
          loadedVariant = null;
          return;
        }
        if (!HlsPlayer.isSupported()) {
          if (video.canPlayType("application/vnd.apple.mpegurl")) {
            video.src = sources.hls;
            video.load();
            play();
          } else loadMp4();
          return;
        }
        hls = new HlsPlayer({
          autoStartLoad: false,
          startLevel: -1,
          abrEwmaDefaultEstimate: 8_000_000,
          abrEwmaDefaultEstimateMax: 12_000_000,
          maxBufferLength: 12,
          maxMaxBufferLength: 20,
          backBufferLength: 4,
        });
        hls.on(HlsPlayer.Events.MANIFEST_PARSED, play);
        hls.on(HlsPlayer.Events.LEVEL_SWITCHED, (_event, data) => {
          const level = hls?.levels[data.level];
          if (level) video.dataset.quality = `${level.width}x${level.height}`;
        });
        hls.on(HlsPlayer.Events.ERROR, (_event, data) => {
          if (!data.fatal) return;
          if (data.type === HlsPlayer.ErrorTypes.MEDIA_ERROR && !mediaRecovered) {
            mediaRecovered = true;
            hls?.recoverMediaError();
          } else {
            loadMp4();
          }
        });
        hls.loadSource(sources.hls);
        hls.attachMedia(video);
      } catch {
        if (!disposed && current === generation) loadMp4();
      }
    };
    const syncPlayback = () => {
      if (canPlay()) {
        void initialize();
        play();
      } else {
        video.pause();
        if (hls && loading) {
          hls.stopLoad();
          loading = false;
        }
        if (reducedMotion.matches) {
          generation++;
          loadedVariant = null;
          hls?.destroy();
          hls = null;
          setHasPlayed(false);
          video.removeAttribute("src");
          video.load();
        }
      }
    };
    const onPlaying = () => setHasPlayed(true);
    const onError = () => {
      if (!hls && !failed) loadMp4();
    };
    const onLayoutChange = () => {
      failed = false;
      syncPlayback();
    };
    video.addEventListener("playing", onPlaying);
    video.addEventListener("canplay", play);
    video.addEventListener("error", onError);
    document.addEventListener("visibilitychange", syncPlayback);
    mobile.addEventListener("change", onLayoutChange);
    reducedMotion.addEventListener("change", syncPlayback);
    const observer = "IntersectionObserver" in window ? new IntersectionObserver(([entry]) => {
      nearViewport = entry.isIntersecting;
      syncPlayback();
    }, { rootMargin: "200px" }) : null;
    if (observer) observer.observe(video);
    else {
      nearViewport = true;
      void initialize();
    }

    return () => {
      disposed = true;
      generation++;
      observer?.disconnect();
      video.removeEventListener("playing", onPlaying);
      video.removeEventListener("canplay", play);
      video.removeEventListener("error", onError);
      document.removeEventListener("visibilitychange", syncPlayback);
      mobile.removeEventListener("change", onLayoutChange);
      reducedMotion.removeEventListener("change", syncPlayback);
      hls?.destroy();
      video.pause();
      video.removeAttribute("src");
      video.load();
    };
  }, [adaptive]);

  return { videoRef, hasPlayed };
}
